import { useRef, useState, type ChangeEvent } from 'react';
import { ISSUE_COMMENT_MAX_LENGTH } from '@d-orca/shared/client/issues';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';
import { useAuth } from '@contexts/useAuth';
import { useTranslation } from 'react-i18next';
import { useToast } from '@components/ui/Toasts/toastContext';
import {
  PHOTO_ACCEPT,
  PHOTO_REFUSAL_KEY,
  photoRefusal,
  uploadRefusal,
  type PhotoRefusal,
} from '@components/Pages/SchedulerPage/lib/photoRefusal';
import { useAddImageComment } from '../hooks/useAddImageComment';
import { useAddTextComment } from '../hooks';
import styles from './HubComposer.module.css';
import { isImeComposing } from '@utils/ime';


interface IHubComposerProps {
  siteId: string;
  issueId: string;
}

/**
 * The issue Hub's comment composer. The typed text stays until the server has
 * the comment — a failed send keeps it (and says so) for another try — and it
 * is capped at the server's length, with a counter. A photo the upload would
 * refuse (type, size) is refused here with the reason, as is one the server
 * still refuses.
 */
export function HubComposer({ siteId, issueId }: IHubComposerProps) {
  const { t } = useTranslation();
  const pushToast = useToast();
  const [text, setText] = useState('');
  const [photoFailure, setPhotoFailure] = useState<PhotoRefusal | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Both report their own failure: the photo's reason inline, the text's as a toast.
  const addImage = useAddImageComment();
  const addText = useAddTextComment(siteId, issueId, useAuth().user?.username, OWNS_ERROR_FEEDBACK);

  function handleAttachClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file fires onChange again
    e.target.value = '';
    if (!file) return;
    const refusal = photoRefusal(file);
    setPhotoFailure(refusal);
    if (refusal) return;
    addImage.mutate({ siteId, issueId, file }, { onError: (error) => setPhotoFailure(uploadRefusal(error)) });
  }

  function handleSend() {
    const trimmed = text.trim();
    if (!trimmed || addText.isPending) return;
    addText.mutate(trimmed, {
      onSuccess: () => setText(''),
      onError: () => pushToast({ kind: 'error', title: t('issues.hub.composer.sendError') }),
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline. Standard chat-composer UX.
    // Not while an IME is composing — that Enter accepts a kanji candidate.
    if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className={styles.composer}>
      <button
        type="button"
        className={styles.attachBtn}
        onClick={handleAttachClick}
        disabled={addImage.isPending}
        aria-label={t('issues.hub.composer.attach')}
      >
        +
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        className={styles.fileInputHidden}
        onChange={handleFileChange}
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('issues.hub.composer.placeholder')}
        rows={2}
        maxLength={ISSUE_COMMENT_MAX_LENGTH}
        // Held while sending: the text is cleared only once the server has it.
        readOnly={addText.isPending}
        className={styles.textarea}
      />
      <button
        type="button"
        className={styles.sendBtn}
        onClick={handleSend}
        disabled={!text.trim() || addText.isPending}
      >
        {t('issues.hub.composer.send')}
      </button>
      <div className={styles.footnote}>
        {photoFailure && (
          <span className={styles.error} role="alert">
            {t(PHOTO_REFUSAL_KEY[photoFailure])}
          </span>
        )}
        <span className={styles.counter}>
          {t('issues.hub.composer.count', { length: text.length, max: ISSUE_COMMENT_MAX_LENGTH })}
        </span>
      </div>
    </div>
  );
}
